import {
  Card,
  Container,
  Group,
  Menu,
  rem,
  Space,
  Stack,
  Tabs,
  TabsValue,
  Text,
  Title,
  UnstyledButton
} from "@mantine/core";
import {IconChevronDown, IconFileBarcode} from "@tabler/icons-react";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback, useMemo} from "react";
import {DispatchEntity} from "@ots-frontend/ots-data";
import {DispatchDetails} from "./DispatchDetails";
import {useTranslation} from "react-i18next";
import {DispatchAction, DispatchActionMenuDropdown} from "../../menu/DispatchActionMenu/DispatchActionMenu";
import {createContentStyles} from "../../../theme/useContentStyles";
import {TabItem} from "../../tabs/Tabs/Tabs";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {useDispatchAction} from "../../menu/DispatchActionMenu/useDispatchAction";
import {useDisclosure} from "@mantine/hooks";
import {CommentModal} from "../../comment/CommentModal/CommentModal";
import {useDispatchMutation} from "../../../hooks/useDispatchMutation";
import {EntityCommentsCard} from "../common/EntityCommentsCard/EntityCommentsCard";

const useStyles = createContentStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type DispatchProfileProps = PropsWithChildren<{
  dispatch: DispatchEntity
}>

export const DispatchProfile = (props: DispatchProfileProps) => {
  const {dispatch} = props;

  const {classes} = useStyles();

  const navigate = useNavigate();
  const {pathname, search, state} = useLocation()
  const {t} = useTranslation(["common"])

  const cancelDisclosure = useDisclosure(false)

  const tabs = useMemo(() => {
    const tabs: TabItem[] = [
      {
        key: "details",
        value: "Details"
      },
      {
        key: "comments",
        value: "Comments"
      },
    ]

    if (dispatch.pickupTransportId && dispatch.deliveryTransportId) {
      tabs.push({
        key: "ticket",
        value: "Ticket",
        icon: <IconFileBarcode size={14}/>,
      })
    }

    return tabs
  }, [dispatch])

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key} icon={tab.icon}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  const {
    onDispatchActionClick,
  } = useDispatchAction()

  const {
    cancelDispatch,
    isUpdating,
  } = useDispatchMutation()

  const onTabChange = useCallback((value: TabsValue) => {
    if (value !== 'ticket') {
      navigate(pathname + search, {replace: true, state: {...state, tabValue: value}})
    } else if (value === 'ticket') {
      navigate(`/tickets/profile?pid=${dispatch.pickupTransportId}&did=${dispatch.deliveryTransportId}`)
    }
  }, [navigate, pathname, search, state, dispatch])

  const _onDispatchActionClick = useCallback((action: DispatchAction) => {
    if (action === "CANCEL") {
      cancelDisclosure[1].open()
    } else {
      onDispatchActionClick(dispatch, action)
    }
  }, [dispatch, onDispatchActionClick, cancelDisclosure])

  const onCancelSubmit = useCallback((comments: string | undefined) => {
    if (comments) {
      cancelDispatch(dispatch.asTypeValue(), comments, undefined)
      cancelDisclosure[1].close()
    }
  }, [dispatch, cancelDispatch, cancelDisclosure])

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>
            {t("common.entity.dispatch.profile.title", "Dispatch Details")}
          </Title>
          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <DispatchActionMenuDropdown
                onActionClick={_onDispatchActionClick}
                dispatch={dispatch}/>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Stack>
            <Tabs
              defaultValue={state?.tabValue ?? "details"}
              onTabChange={onTabChange}
              p="sm"
            >
              <Tabs.List>{items}</Tabs.List>
              <Space h="md"/>
              <Tabs.Panel value="details">
                <DispatchDetails dispatch={dispatch}/>
              </Tabs.Panel>
              <Tabs.Panel value="comments">
                <EntityCommentsCard entity={dispatch}/>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        </Container>
      </Card.Section>

      <CommentModal
        title={t<string>("common.action.cancel", "Cancel")}
        opened={cancelDisclosure[0]}
        onClose={cancelDisclosure[1].close}
        onSubmit={onCancelSubmit}
        loading={isUpdating}
        color="red"
        message={
          <Text size="sm">
            {t("common.profile.entity.dispatch.action.cancel.alert.message", "Are you sure you want to cancel this dispatch?")}
          </Text>
        }/>

    </Card>
  );
};
